import React, { useState, useContext, useEffect } from "react";
import { Download } from "react-feather";
import { IconX } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { InviteAffiliateButton } from "../InviteAffiliateButton/InviteAffiliateButton";
import { useModal } from "../../contexts/modal/hooks";
import { ImportAffiliatesModal } from "../ImportAffiliatesModal/ImportAffiliatesModal";
import { BrandContext } from "../../contexts/BrandContext/context";
import { Button } from "../ui/button";
import { brandService } from "../../services/brand.service";
import { JoinPortalLinkPopover } from "./JoinPortalLinkPopover";

interface ActionButton {
  id: string;
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary";
  icon?: React.ReactNode;
}

interface PageHeaderProps {
  title: string;
  description?: string;
  /** Greets the person by name in place of the title. */
  username?: string;
  showDefaultActions?: boolean;
  customActions?: ActionButton[];
  /** A detail or form page: the route its close control returns to. The
      control is the one way back and always sits in the top-right corner. */
  closeTo?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description = "",
  username,
  showDefaultActions = false,
  customActions = [],
  closeTo,
}) => {
  const { openModal } = useModal();
  const [showImportModal, setShowImportModal] = useState(false);
  const [bulkImportEnabled, setBulkImportEnabled] = useState(true);
  const { brandDetails, isLoading: isBrandLoading } = useContext(BrandContext);

  useEffect(() => {
    if (!showDefaultActions) return;
    brandService
      .getBulkInviteSettings()
      .then((settings) => setBulkImportEnabled(settings.bulkImportEnabled))
      .catch(() => setBulkImportEnabled(true));
  }, [showDefaultActions]);

  const handleInvite = () => {
    openModal();
  };

  const renderDefaultActions = () => (
    <>
      {!brandDetails && isBrandLoading && (
        // Same footprint as the Join Portal Link button so it doesn't shift
        // its neighbours when brand details finish loading
        <div
          aria-hidden
          className="h-10 w-24 sm:w-40 rounded-md bg-muted animate-pulse min-w-fit flex-shrink-0"
        />
      )}
      {brandDetails && brandDetails.username && (
        <div className="min-w-fit flex-shrink-0">
          {/* One link per program — brands share the link instead of assigning
              affiliates to each extra program by hand. */}
          <JoinPortalLinkPopover username={brandDetails.username} />
        </div>
      )}
      <span
        className="min-w-fit flex-shrink-0"
        title={
          bulkImportEnabled
            ? undefined
            : "Contact support to provide more information and enable bulk invite"
        }
      >
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            if (!bulkImportEnabled) return;
            setShowImportModal(true);
          }}
          disabled={!bulkImportEnabled}
          aria-disabled={!bulkImportEnabled}
          className="min-h-[40px] min-w-fit flex-shrink-0"
        >
          <Download className="h-4 w-4" />
          <span className="hidden sm:inline">Import affiliates</span>
          <span className="sm:hidden">Import</span>
        </Button>
      </span>
      <div className="min-w-fit flex-shrink-0">
        <InviteAffiliateButton onClick={handleInvite} />
      </div>
    </>
  );

  const renderCustomActions = () =>
    customActions.map((action, index) => (
      <Button
        id={action.id}
        key={index}
        onClick={action.onClick}
        variant={action.variant === "primary" ? "default" : "outline"}
        className="min-h-[40px] min-w-fit flex-shrink-0"
      >
        {action.icon && action.icon}
        <span>{action.label}</span>
      </Button>
    ));

  return (
    <div className="mb-6 sm:mb-8">
      <div className="flex w-full flex-col xl:flex-row justify-between gap-4">
        <div className="flex-1 shrink-0">
          <h1 className="text-2xl font-semibold tracking-tight">
            {username ? `Welcome back, ${username}` : title}
          </h1>
          {description && (
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          )}
        </div>

        {(showDefaultActions || customActions.length > 0 || closeTo) && (
          <div
            id="invite-affiliates"
            className="flex flex-wrap items-start gap-2 sm:gap-3"
          >
            {showDefaultActions && renderDefaultActions()}
            {customActions.length > 0 && renderCustomActions()}
            {closeTo && (
              <Link
                to={closeTo}
                aria-label="Close"
                title="Close"
                className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg border border-input bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <IconX size={18} stroke={1.75} />
              </Link>
            )}
          </div>
        )}
      </div>

      {showDefaultActions && (
        <ImportAffiliatesModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
        />
      )}
    </div>
  );
};

export default PageHeader;
